import '../../webparts/blu.scss';
import * as React from 'react';
import { useEffect } from 'react';
import { useStore } from '../stores/root-store-context';
import { observer } from 'mobx-react-lite';
import { Button, Flex, Modal, Select, Spin, Typography } from 'antd';
import { ExclamationCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { DefaultOptionType } from 'antd/es/select';
import type { AxiosError } from 'axios';
import { PropertyUpdateResponse, PropertyUpdateValidationError, PropertyUpdateServerError } from '../api/ApplicationApi';
import { useDispatch } from 'react-redux';
import Headers from "../../../../../redux/Actions/Headers/Header";
const { Title } = Typography;

// Discriminated result of a BuilderLinkUpdate call, keyed by the backend's HTTP status.
type BluResult =
  | { kind: 'success'; data: PropertyUpdateResponse }        // HTTP 200
  | { kind: 'validation'; message: string }                  // HTTP 400
  | { kind: 'error'; message: string };                       // HTTP 500 / network / anything else

const GENERIC_ERROR_MESSAGE = "Your request wasn't successful. Please try again.";

const BluV2 = observer(() => {
  const { applicationStore } = useStore();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isModalLoading, setIsModalLoading] = React.useState(false);
  const [result, setResult] = React.useState<BluResult | null | undefined>(undefined);
  const [selectedOption, setSelectedOption] = React.useState<DefaultOptionType | null>(null);

  const dispatch = useDispatch();

  const onChangeProperty = (value: any, option: DefaultOptionType): void => {
    setSelectedOption(option);
    setIsModalOpen(true);
  }

  useEffect(() => {
    applicationStore.LoadPropertyListAsync()
      .then(() => ({})).catch(() => ({}));
  }, []);

  useEffect(() => {
    dispatch(
      Headers.setHeader({
        title: "BLU - Builder Link Utility",
        actions: [
          {
            type: "div",
            content: (
              <div className="blu__header-search">
                <Spin spinning={applicationStore.isLoading}>
                  <Flex wrap="wrap" gap="small">
                    <Select
                      data-testid="blu-select-property"
                      style={{ width: "300px", maxWidth: "100%" }}
                      showSearch
                      filterOption={(input: string, option?: { label: string; value: number }) => (option?.label ?? '').toLowerCase().indexOf(input.toLowerCase()) > -1}
                      options={applicationStore.Properties}
                      placeholder="Please select the property..."
                      onChange={onChangeProperty}
                      value={selectedOption?.value}
                      dropdownStyle={{ maxWidth: '95vw' }}
                    />
                  </Flex>
                  {result !== undefined ? renderResultModal() : renderConfirmModal()}
                </Spin>
              </div>
            ),
          },
        ],
      })
    );
    return () => {
      dispatch(Headers.resetHeader());
    };
  }, [dispatch, applicationStore.Properties, selectedOption, onChangeProperty]);

  const handleOk = async (): Promise<void> => {
    if (!selectedOption?.value) {
      setResult({ kind: 'error', message: GENERIC_ERROR_MESSAGE });
      return;
    }
    setIsModalLoading(true);
    try {
      // Any non-2xx status makes axios throw and is handled in catch. But axios's
      // default validateStatus resolves ALL 2xx (e.g. 204), so success must be an
      // actual 200 whose body carries both expected fields — otherwise show a
      // controlled error rather than rendering undefined values.
      const res = await applicationStore.applicationApi.updatePropertyAsync(selectedOption.value);
      const data = res.data;
      if (res.status === 200 && data && data.unitBuilderUpdates != null && data.residentBuilderUpdates != null) {
        setResult({ kind: 'success', data });
      } else {
        setResult({ kind: 'error', message: GENERIC_ERROR_MESSAGE });
      }
    } catch (err) {
      const axiosErr = err as AxiosError<PropertyUpdateValidationError | PropertyUpdateServerError>;
      const httpStatus = axiosErr?.response?.status;
      const payload = axiosErr?.response?.data;
      if (httpStatus === 400) {
        // Validation error body: { "error": "Invalid companykey: 0" }
        const validation = payload as PropertyUpdateValidationError | undefined;
        setResult({ kind: 'validation', message: validation?.error || 'The selected property could not be validated.' });
      } else {
        // 500 / network / anything else body: { "message": "...", "traceId": "..." } — traceId is intentionally not shown to the user.
        const serverError = payload as PropertyUpdateServerError | undefined;
        setResult({ kind: 'error', message: serverError?.message || GENERIC_ERROR_MESSAGE });
      }
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleCancel = (): void => {
    setSelectedOption(null);
    setIsModalOpen(false);
    setResult(undefined);
  };

  const renderConfirmModal = (): React.ReactElement => (
    <Modal data-testid="blu-modal-confirm" closeIcon={null} open={isModalOpen} onCancel={handleCancel}
      footer={<>
        <Button data-testid="blu-btn-confirm" key="btn-confirm-ok" type="primary" onClick={handleOk} disabled={!selectedOption?.value}>Confirm</Button>
        <Button data-testid="blu-btn-cancel" key="btn-confirm-cancel" type="default" onClick={handleCancel}>Cancel</Button>
      </>}>
      <Spin spinning={isModalLoading}>
        <p data-testid="blu-text-confirm-message">Please confirm that you would like to update the builder link information for &quot;{selectedOption?.label}&quot;</p>
      </Spin>
    </Modal>
  );

  // HTTP 200 — unchanged success pop-up, now shown ONLY for a real 200 response.
  const renderResultOkModal = (data: PropertyUpdateResponse): React.ReactElement => (
    <Modal data-testid="blu-modal-success" closeIcon={null} open={isModalOpen} onCancel={handleCancel}
      footer={<>
        <Button data-testid="blu-btn-select-another" key="btn-result-ok" type="primary" onClick={handleCancel}>Select another property</Button>
      </>}>
      <Spin spinning={isModalLoading}>
        <p data-testid="blu-text-success-message">Update has been completed for &quot;{selectedOption?.label}&quot; </p>
        <p data-testid="blu-text-unit-updates">Unit builder updates: {data.unitBuilderUpdates} rows</p>
        <p data-testid="blu-text-resident-updates">Resident builder updates: {data.residentBuilderUpdates} rows</p>
      </Spin>
    </Modal>);

  // HTTP 400 — validation error. User-correctable, so we surface the backend message and let them retry.
  const renderResultValidationModal = (message: string): React.ReactElement => (
    <Modal data-testid="blu-modal-validation" closeIcon={null} open={isModalOpen} onCancel={handleCancel}
      footer={<>
        <Button data-testid="blu-btn-validation-retry" key="btn-validation-try" type="primary" onClick={handleOk} disabled={!selectedOption?.value}>Try again</Button>
        <Button data-testid="blu-btn-validation-select-another" key="btn-validation-cancel" type="default" onClick={handleCancel}>Select another property</Button>
      </>}>
      <Spin spinning={isModalLoading}>
        <Flex gap="middle" align="flex-start">
          <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 22, marginTop: 2 }} />
          <div>
            <p data-testid="blu-text-validation-title" style={{ fontWeight: 600, marginBottom: 4 }}>We couldn&apos;t validate this property</p>
            <p data-testid="blu-text-validation-message" style={{ marginBottom: 0 }}>{message}</p>
            <p style={{ marginBottom: 0, marginTop: 8, color: 'rgba(0,0,0,0.45)' }}>No changes were made. Please check the selected property and try again.</p>
          </div>
        </Flex>
      </Spin>
    </Modal>
  );

  // HTTP 500 (or network/unexpected) — server error. Not user-correctable; we show the message only.
  const renderResultServerErrorModal = (message: string): React.ReactElement => (
    <Modal data-testid="blu-modal-error" closeIcon={null} open={isModalOpen} onCancel={handleCancel}
      footer={<>
        <Button data-testid="blu-btn-retry" key="btn-error-try" type="primary" onClick={handleOk}>Try again</Button>
        <Button data-testid="blu-btn-cancel-error" key="btn-error-cancel" type="default" onClick={handleCancel}>Cancel</Button>
      </>}>
      <Spin spinning={isModalLoading}>
        <Flex gap="middle" align="flex-start">
          <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 22, marginTop: 2 }} />
          <div>
            <p data-testid="blu-text-error-title" style={{ fontWeight: 600, marginBottom: 4 }}>Something went wrong during the update</p>
            <p data-testid="blu-text-error-message" style={{ marginBottom: 0 }}>{message}</p>
            <p style={{ marginBottom: 0, marginTop: 8, color: 'rgba(0,0,0,0.45)' }}>Please try again. If the problem persists, contact support.</p>
          </div>
        </Flex>
      </Spin>
    </Modal>
  );

  const renderResultModal = (): React.ReactElement => {
    switch (result?.kind) {
      case 'success':
        return renderResultOkModal(result.data);
      case 'validation':
        return renderResultValidationModal(result.message);
      default:
        return renderResultServerErrorModal(result?.message ?? GENERIC_ERROR_MESSAGE);
    }
  };

  if (!applicationStore.IsAuthorized) {
    return <Title level={3}>We&apos;re Sorry Your Request is Unauthorized</Title>
  }

  if (applicationStore.loadError) {
    return (
      <Title level={5} type="secondary">
        Unable to load properties. Please check the BLU API configuration or try again later.
      </Title>
    );
  }

  return (
    <div className="blu__empty-message">
      Please select a property using the selector in the top right corner.
    </div>
  );
});

export default BluV2;